import { useState, useEffect } from "react";

// ─── Google Fonts ───────────────────────────────────────────────────────────
const fontLink = document.createElement("link");
fontLink.href =
  "https://fonts.googleapis.com/css2?family=Lora:wght@500;600;700&family=Outfit:wght@300;400;500;600;700&display=swap";
fontLink.rel = "stylesheet";
document.head.appendChild(fontLink);

// ─── Design Tokens ──────────────────────────────────────────────────────────
const T = {
  forest:  "#1a3a1e",
  moss:    "#2d5a2e",
  sage:    "#4e8c50",
  lime:    "#7ec47f",
  amber:   "#d4a017",
  gold:    "#f0c040",
  cream:   "#f7f2e8",
  parchment:"#ede5d0",
  bark:    "#6b4c2a",
  soil:    "#3a2510",
  sky:     "#2d6fa4",
  mist:    "#e8f4e9",
  danger:  "#c0392b",
  white:   "#ffffff",
  textDark:"#1c2b1d",
  textMid: "#4a6741",
  textSoft:"#7a9470",
};

// ─── Styles ─────────────────────────────────────────────────────────────────
const css = `
  @keyframes fadeUp {
    from { opacity:0; transform:translateY(18px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes slideIn {
    from { opacity:0; transform:translateX(30px); }
    to   { opacity:1; transform:translateX(0); }
  }
  @keyframes pulse {
    0%,100% { opacity:1; } 50% { opacity:.5; }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Outfit',sans-serif; background:${T.cream}; }
  ::-webkit-scrollbar { width:6px; }
  ::-webkit-scrollbar-track { background:${T.parchment}; }
  ::-webkit-scrollbar-thumb { background:${T.sage}; border-radius:3px; }
`;
const styleEl = document.createElement("style");
styleEl.textContent = css;
document.head.appendChild(styleEl);

// ─── Feature Definitions ────────────────────────────────────────────────────
const features = [
  {
    id: "farmer-info",
    icon: "👤",
    title: "Farmer Info",
    subtitle: "Name · Location · Region · Date",
    accent: T.moss,
    category: "Profile",
    badge: null,
    preview: "Arjun Patil · Maharashtra",
    detail: {
      description: "Manage your complete farmer profile including personal details, farm location coordinates, active region, and registration date.",
      stats: [
        { label: "Farmer ID", value: "MH-2891-AP" },
        { label: "Region", value: "Vidarbha, MH" },
        { label: "Farm Area", value: "4.2 Acres" },
        { label: "Member Since", value: "Jan 2022" },
      ],
      actions: ["Edit Profile", "Download ID Card", "Update Location"],
    },
  },
  {
    id: "weather",
    icon: "⛅",
    title: "Weather Update",
    subtitle: "Season · Forecast · Alerts",
    accent: T.sky,
    category: "Live",
    badge: { label: "Live", color: T.sky, bg: "#dceef9" },
    preview: "28°C · Partly Cloudy",
    detail: {
      description: "Real-time weather data for your farm location — temperature, humidity, rainfall forecast, and season-specific advisories.",
      stats: [
        { label: "Temperature", value: "28°C / 18°C" },
        { label: "Humidity", value: "62%" },
        { label: "Rainfall (7d)", value: "12 mm" },
        { label: "Season", value: "Kharif 2026" },
      ],
      actions: ["7-Day Forecast", "Set Weather Alert", "Season Calendar"],
    },
  },
  {
    id: "market-price",
    icon: "📈",
    title: "Market Price",
    subtitle: "Live mandi rates · Today",
    accent: T.amber,
    category: "Live",
    badge: { label: "Today", color: T.bark, bg: "#f5e8ca" },
    preview: "Wheat ₹2,340/qtl ↑",
    detail: {
      description: "Real-time commodity prices from nearest APMC mandis. Compare rates across mandis and track price trends for your crops.",
      stats: [
        { label: "Wheat", value: "₹2,340 / qtl" },
        { label: "Soybean", value: "₹4,850 / qtl" },
        { label: "Cotton", value: "₹6,200 / qtl" },
        { label: "Onion", value: "₹1,120 / qtl" },
      ],
      actions: ["Compare Mandis", "Set Price Alert", "Historical Trends"],
    },
  },
  {
    id: "risk-alert",
    icon: "🚨",
    title: "Risk Alert",
    subtitle: "Pest · Disease · Climate risks",
    accent: T.danger,
    category: "Alerts",
    badge: { label: "2 Active", color: T.danger, bg: "#fde8e6" },
    preview: "Pest alert: Aphids detected",
    detail: {
      description: "AI-powered early warning system for crop diseases, pest infestations, extreme weather events, and soil health risks.",
      stats: [
        { label: "Active Alerts", value: "2 High" },
        { label: "Last Risk", value: "Aphid Infestation" },
        { label: "Risk Zone", value: "Block C, D" },
        { label: "Resolved (30d)", value: "5 Issues" },
      ],
      actions: ["View All Alerts", "Call Agri Expert", "Mark Resolved"],
    },
  },
  {
    id: "farm-accounting",
    icon: "📒",
    title: "Farm Accounting",
    subtitle: "Income · Expense · Profit",
    accent: T.moss,
    category: "Finance",
    badge: null,
    preview: "Net profit ₹48,200 this season",
    detail: {
      description: "Track all farm-related income and expenses. Get season-wise P&L reports, loan tracking, and subsidy management in one place.",
      stats: [
        { label: "Total Income", value: "₹1,24,500" },
        { label: "Total Expense", value: "₹76,300" },
        { label: "Net Profit", value: "₹48,200" },
        { label: "Pending Loans", value: "₹25,000" },
      ],
      actions: ["Add Transaction", "View P&L Report", "Export to PDF"],
    },
  },
  {
    id: "crop-quality",
    icon: "🔬",
    title: "Crop Quality Analysis",
    subtitle: "Grade · Moisture · Purity",
    accent: T.sage,
    category: "Analysis",
    badge: { label: "AI", color: T.sky, bg: "#dceef9" },
    preview: "Wheat Grade: A+ (94%)",
    detail: {
      description: "Upload crop photos or connect IoT sensors to get AI-driven quality grading, moisture content analysis, and market-readiness scores.",
      stats: [
        { label: "Current Grade", value: "A+ (94%)" },
        { label: "Moisture", value: "11.2%" },
        { label: "Purity", value: "98.5%" },
        { label: "Last Tested", value: "28 Mar 2026" },
      ],
      actions: ["Upload Sample Photo", "Request Lab Test", "View Quality History"],
    },
  },
  {
    id: "cost-control",
    icon: "💰",
    title: "Cost Control",
    subtitle: "Input costs · Budget tracking",
    accent: T.amber,
    category: "Finance",
    badge: null,
    preview: "Fertilizer spend ↓ 12%",
    detail: {
      description: "Monitor and optimize your input costs — seeds, fertilizers, pesticides, labour and irrigation. Get budget vs. actual comparisons.",
      stats: [
        { label: "Budget (Season)", value: "₹80,000" },
        { label: "Spent So Far", value: "₹58,400" },
        { label: "Remaining", value: "₹21,600" },
        { label: "Overspent Items", value: "Labour (8%)" },
      ],
      actions: ["Add Expense", "Set Budget Alert", "Cost Comparison"],
    },
  },
  {
    id: "my-crop-info",
    icon: "🌱",
    title: "My Crop Info",
    subtitle: "Active crops · Growth stage",
    accent: T.sage,
    category: "Crops",
    badge: null,
    preview: "3 Active · Wheat at tillering",
    detail: {
      description: "View all your active crops with current growth stage, sowing date, expected harvest, and recommended interventions for each.",
      stats: [
        { label: "Active Crops", value: "3 Crops" },
        { label: "Main Crop", value: "Wheat (2.5 ac)" },
        { label: "Growth Stage", value: "Tillering" },
        { label: "Days to Harvest", value: "42 Days" },
      ],
      actions: ["Add New Crop", "Update Growth Stage", "View Crop Timeline"],
    },
  },
  {
    id: "crop-suggestion",
    icon: "💡",
    title: "Crop Suggestion",
    subtitle: "AI-based crop recommendations",
    accent: T.lime,
    category: "AI Advisor",
    badge: { label: "AI Powered", color: T.sky, bg: "#dceef9" },
    preview: "Soybean best fit for Kharif",
    detail: {
      description: "Get personalized crop recommendations based on your soil type, region, season, water availability, and current market demand.",
      stats: [
        { label: "Top Suggestion", value: "Soybean" },
        { label: "Expected ROI", value: "38% margin" },
        { label: "Water Need", value: "Medium" },
        { label: "Market Demand", value: "High (Jun–Sep)" },
      ],
      actions: ["Get New Suggestions", "Compare Crops", "Save Recommendation"],
    },
  },
  {
    id: "crop-selling",
    icon: "🚜",
    title: "Crop Selling Option",
    subtitle: "Buyers · Mandi · Contract farming",
    accent: T.bark,
    category: "Market",
    badge: { label: "New Offer", color: "#2e7d32", bg: "#e8f5e9" },
    preview: "3 buyers interested in wheat",
    detail: {
      description: "Connect directly with buyers, list your produce on digital mandis, explore contract farming opportunities, and compare sale options.",
      stats: [
        { label: "Active Buyers", value: "3 Interested" },
        { label: "Best Offer", value: "₹2,410/qtl" },
        { label: "Nearest Mandi", value: "Nagpur (12 km)" },
        { label: "Contract Offers", value: "1 Available" },
      ],
      actions: ["View Buyer Offers", "List My Produce", "Explore Contracts"],
    },
  },
  {
    id: "multi-language",
    icon: "🌐",
    title: "Multi-language Support",
    subtitle: "Hindi · Marathi · English · More",
    accent: T.sky,
    category: "Settings",
    badge: null,
    preview: "12 languages available",
    detail: {
      description: "Switch the entire app interface to your preferred regional language. Supports voice input and audio notifications in local dialects.",
      stats: [
        { label: "Current Language", value: "English" },
        { label: "Available", value: "12 Languages" },
        { label: "Voice Input", value: "Enabled" },
        { label: "Audio Alerts", value: "Marathi" },
      ],
      actions: ["Change Language", "Enable Voice Input", "Set Audio Language"],
    },
  },
  {
    id: "scan-photo",
    icon: "📷",
    title: "Scan & Photo Crop Info",
    subtitle: "Identify disease · Nutrient check",
    accent: T.moss,
    category: "AI Advisor",
    badge: { label: "AI Scan", color: T.sky, bg: "#dceef9" },
    preview: "Scan leaf for instant diagnosis",
    detail: {
      description: "Point your camera at any crop leaf, soil, or plant. Our AI instantly identifies diseases, deficiencies, pests, and gives treatment advice.",
      stats: [
        { label: "Scans Done", value: "24 Total" },
        { label: "Issues Found", value: "3 Diseases" },
        { label: "Accuracy", value: "94.2%" },
        { label: "Last Scan", value: "26 Mar 2026" },
      ],
      actions: ["Open Camera", "Upload Photo", "View Scan History"],
    },
  },
  {
    id: "crop-history",
    icon: "📅",
    title: "Crop History",
    subtitle: "Irrigation · Spray · Harvest log",
    accent: T.forest,
    category: "Records",
    badge: null,
    preview: "Last irrigation: 25 Mar 2026",
    detail: {
      description: "Complete log of all farm activities — irrigation schedules, fertilizer application, pesticide spray, and harvest records for every crop.",
      stats: [
        { label: "Last Irrigation", value: "25 Mar 2026" },
        { label: "Last Spray", value: "18 Mar 2026" },
        { label: "Total Logs", value: "142 Entries" },
        { label: "Seasons Recorded", value: "4 Seasons" },
      ],
      actions: ["Add Activity Log", "View Full History", "Export Records"],
    },
  },
  {
    id: "crop-equipment",
    icon: "🔧",
    title: "Crop Equipment Details",
    subtitle: "Tractors · Tools · Maintenance",
    accent: T.bark,
    category: "Assets",
    badge: null,
    preview: "2 tractors · Next service: Apr 5",
    detail: {
      description: "Track all farm equipment — ownership details, maintenance schedules, rental availability, fuel logs, and repair history.",
      stats: [
        { label: "Total Equipment", value: "8 Items" },
        { label: "Tractors", value: "2 Units" },
        { label: "Next Service", value: "Apr 5, 2026" },
        { label: "Rental Income", value: "₹4,200/mo" },
      ],
      actions: ["Add Equipment", "Schedule Service", "List for Rent"],
    },
  },
];

const categoryColors = {
  Profile:   { bg: "#e8f5e9", text: "#2e7d32" },
  Live:      { bg: "#e3f2fd", text: "#1565c0" },
  Alerts:    { bg: "#fde8e6", text: "#c62828" },
  Finance:   { bg: "#fff8e1", text: "#f57f17" },
  Analysis:  { bg: "#e8f5e9", text: "#2e7d32" },
  Crops:     { bg: "#f1f8e9", text: "#33691e" },
  "AI Advisor":{ bg: "#e8eaf6", text: "#283593" },
  Market:    { bg: "#fff3e0", text: "#e65100" },
  Settings:  { bg: "#e0f7fa", text: "#006064" },
  Records:   { bg: "#efebe9", text: "#4e342e" },
  Assets:    { bg: "#fce4ec", text: "#880e4f" },
};

// ─── Components ─────────────────────────────────────────────────────────────

function Badge({ label, color, bg }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: bg, color, fontSize: 11, fontWeight: 600,
      padding: "2px 8px", borderRadius: 20, letterSpacing: "0.03em",
    }}>
      <span style={{
        width: 5, height: 5, borderRadius: "50%", background: color,
        animation: "pulse 1.8s infinite",
      }} />
      {label}
    </span>
  );
}

function CategoryTag({ category }) {
  const col = categoryColors[category] || { bg: "#eee", text: "#555" };
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, letterSpacing: "0.06em",
      textTransform: "uppercase", color: col.text, background: col.bg,
      padding: "2px 6px", borderRadius: 4,
    }}>{category}</span>
  );
}

function FeatureCard({ feature, onClick, delay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={() => onClick(feature)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        borderRadius: 16,
        border: `1px solid ${hovered ? feature.accent + "55" : "#e0ddd4"}`,
        padding: "18px 18px 14px",
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.22s cubic-bezier(.4,0,.2,1)",
        transform: hovered ? "translateY(-4px)" : "none",
        boxShadow: hovered
          ? `0 12px 32px ${feature.accent}22, 0 2px 8px rgba(0,0,0,0.06)`
          : "0 2px 8px rgba(0,0,0,0.04)",
        animation: `fadeUp 0.45s ${delay}s both`,
      }}
    >
      {/* Accent top bar */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${feature.accent}, ${feature.accent}88)`,
        borderRadius: "16px 16px 0 0",
      }} />

      {/* Arrow indicator */}
      <div style={{
        position: "absolute", top: 14, right: 14,
        color: feature.accent, opacity: hovered ? 1 : 0,
        transition: "opacity 0.2s, transform 0.2s",
        transform: hovered ? "translate(0,0)" : "translate(-4px, 4px)",
        fontSize: 14, fontWeight: 700,
      }}>↗</div>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: feature.accent + "18",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, flexShrink: 0,
          transition: "transform 0.2s",
          transform: hovered ? "scale(1.1)" : "scale(1)",
        }}>{feature.icon}</div>
        <CategoryTag category={feature.category} />
      </div>

      <div style={{
        fontFamily: "'Lora', serif",
        fontSize: 15, fontWeight: 600,
        color: T.textDark, marginBottom: 3, lineHeight: 1.3,
      }}>{feature.title}</div>

      <div style={{
        fontSize: 12, color: T.textSoft, fontWeight: 300, marginBottom: 10,
      }}>{feature.subtitle}</div>

      {/* Preview chip */}
      <div style={{
        fontSize: 11.5, color: feature.accent, fontWeight: 500,
        background: feature.accent + "12", padding: "4px 10px",
        borderRadius: 8, display: "inline-block",
      }}>{feature.preview}</div>

      {/* Badge */}
      {feature.badge && (
        <div style={{ marginTop: 8 }}>
          <Badge {...feature.badge} />
        </div>
      )}
    </div>
  );
}

function DetailPanel({ feature, onClose }) {
  const [activeTab, setActiveTab] = useState("overview");
  useEffect(() => { setActiveTab("overview"); }, [feature]);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 100,
      display: "flex", alignItems: "flex-end",
      background: "rgba(15,30,15,0.55)", backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 540, margin: "0 auto",
          background: T.white, borderRadius: "24px 24px 0 0",
          overflow: "hidden", maxHeight: "85vh", overflowY: "auto",
          animation: "fadeUp 0.3s both",
        }}
      >
        {/* Panel header */}
        <div style={{
          background: `linear-gradient(135deg, ${feature.accent}, ${feature.accent}cc)`,
          padding: "28px 24px 20px",
          position: "relative",
        }}>
          <button onClick={onClose} style={{
            position: "absolute", top: 16, right: 16,
            background: "rgba(255,255,255,0.2)", border: "none",
            color: "#fff", width: 32, height: 32, borderRadius: "50%",
            cursor: "pointer", fontSize: 16, display: "flex",
            alignItems: "center", justifyContent: "center",
            transition: "background 0.2s",
          }}>✕</button>
          <div style={{ fontSize: 36, marginBottom: 8 }}>{feature.icon}</div>
          <div style={{
            fontFamily: "'Lora', serif",
            fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 4,
          }}>{feature.title}</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", fontWeight: 300 }}>
            {feature.subtitle}
          </div>
          <div style={{ marginTop: 10 }}>
            <CategoryTag category={feature.category} />
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: "flex", borderBottom: "1px solid #eee",
          background: "#fafaf8",
        }}>
          {["overview", "stats", "actions"].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              flex: 1, padding: "12px 0",
              background: "none", border: "none",
              borderBottom: activeTab === tab ? `2px solid ${feature.accent}` : "2px solid transparent",
              color: activeTab === tab ? feature.accent : T.textSoft,
              fontFamily: "'Outfit', sans-serif",
              fontSize: 13, fontWeight: activeTab === tab ? 600 : 400,
              cursor: "pointer", textTransform: "capitalize",
              transition: "all 0.2s",
            }}>{tab}</button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ padding: "20px 24px 32px" }}>
          {activeTab === "overview" && (
            <div style={{ animation: "slideIn 0.25s both" }}>
              <p style={{
                fontSize: 14.5, color: T.textDark, lineHeight: 1.7,
                fontWeight: 300, marginBottom: 20,
              }}>{feature.detail.description}</p>
              <div style={{
                background: feature.accent + "0d", borderRadius: 12,
                border: `1px solid ${feature.accent}22`, padding: "14px 16px",
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, letterSpacing: "0.08em",
                  textTransform: "uppercase", color: feature.accent, marginBottom: 8,
                }}>Quick Snapshot</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {feature.detail.stats.map(s => (
                    <div key={s.label}>
                      <div style={{ fontSize: 11, color: T.textSoft }}>{s.label}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.textDark }}>{s.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "stats" && (
            <div style={{ animation: "slideIn 0.25s both" }}>
              {feature.detail.stats.map((s, i) => (
                <div key={s.label} style={{
                  display: "flex", justifyContent: "space-between",
                  alignItems: "center", padding: "13px 0",
                  borderBottom: i < feature.detail.stats.length - 1 ? "1px solid #f0ede6" : "none",
                  animation: `fadeUp 0.3s ${i * 0.07}s both`,
                }}>
                  <span style={{ fontSize: 13, color: T.textSoft }}>{s.label}</span>
                  <span style={{
                    fontSize: 14, fontWeight: 600, color: T.textDark,
                    background: feature.accent + "12", padding: "3px 10px",
                    borderRadius: 8,
                  }}>{s.value}</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === "actions" && (
            <div style={{ animation: "slideIn 0.25s both", display: "flex", flexDirection: "column", gap: 10 }}>
              {feature.detail.actions.map((action, i) => (
                <button key={action} style={{
                  background: i === 0
                    ? `linear-gradient(135deg, ${feature.accent}, ${feature.accent}cc)`
                    : "#f5f2ec",
                  color: i === 0 ? "#fff" : T.textDark,
                  border: i === 0 ? "none" : "1px solid #e0ddd4",
                  borderRadius: 12, padding: "14px 18px",
                  fontSize: 14, fontWeight: i === 0 ? 600 : 400,
                  cursor: "pointer", textAlign: "left",
                  fontFamily: "'Outfit', sans-serif",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  transition: "all 0.2s",
                  animation: `fadeUp 0.3s ${i * 0.07}s both`,
                  boxShadow: i === 0 ? `0 4px 16px ${feature.accent}44` : "none",
                }}>
                  <span>{action}</span>
                  <span style={{ opacity: 0.7 }}>→</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main App ────────────────────────────────────────────────────────────────
export function BhuPilot() {
  const [selected, setSelected] = useState(null);
  const [search, setSearch]     = useState("");
  const [filter, setFilter]     = useState("All");

  const categories = ["All", ...Array.from(new Set(features.map(f => f.category)))];

  const filtered = features.filter(f => {
    const matchSearch =
      f.title.toLowerCase().includes(search.toLowerCase()) ||
      f.subtitle.toLowerCase().includes(search.toLowerCase());
    const matchCat = filter === "All" || f.category === filter;
    return matchSearch && matchCat;
  });

  return (
    <div style={{ minHeight: "100vh", background: T.cream, fontFamily: "'Outfit', sans-serif" }}>

      {/* ── Header ── */}
      <div style={{
        background: `linear-gradient(135deg, ${T.forest} 0%, ${T.moss} 100%)`,
        padding: "0 20px",
        position: "sticky", top: 0, zIndex: 50,
        boxShadow: "0 2px 16px rgba(0,0,0,0.18)",
      }}>
        <div style={{
          maxWidth: 900, margin: "0 auto",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          height: 60,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 10,
              background: "linear-gradient(135deg, #7ec47f, #4e8c50)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 18, boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            }}>🌾</div>
            <div>
              <div style={{
                fontFamily: "'Lora', serif",
                fontSize: 17, fontWeight: 700, color: T.cream,
                letterSpacing: "0.01em",
              }}>FarmSync</div>
              <div style={{ fontSize: 10, color: "rgba(245,238,220,0.55)", fontWeight: 300, letterSpacing: "0.06em" }}>
                FARMER DASHBOARD
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 20, padding: "5px 12px",
              display: "flex", alignItems: "center", gap: 6,
              color: "rgba(245,238,220,0.7)", fontSize: 12,
            }}>
              🌐 EN
            </div>
            <div style={{
              width: 34, height: 34, borderRadius: "50%",
              background: "linear-gradient(135deg, #4e8c50, #2d5a2e)",
              border: "2px solid #d4a017",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: T.cream, fontWeight: 700, fontSize: 13,
              cursor: "pointer",
            }}>AP</div>
          </div>
        </div>
      </div>

      {/* ── Farmer Hero ── */}
      <div style={{
        background: `linear-gradient(160deg, ${T.moss} 0%, ${T.forest} 100%)`,
        padding: "20px 20px 24px",
      }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 11, color: "rgba(245,238,220,0.5)", letterSpacing: "0.1em", marginBottom: 4 }}>
                WELCOME BACK
              </div>
              <div style={{
                fontFamily: "'Lora', serif",
                fontSize: 22, fontWeight: 700, color: T.cream, marginBottom: 4,
              }}>Arjun Patil 👋</div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {[["📍", "Vidarbha, MH"], ["🗓️", "30 Mar 2026"], ["🌾", "Kharif Season"]].map(([icon, text]) => (
                  <span key={text} style={{ fontSize: 12, color: "rgba(245,238,220,0.6)", display: "flex", gap: 4 }}>
                    {icon} {text}
                  </span>
                ))}
              </div>
            </div>
            <div style={{
              textAlign: "right",
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 14, padding: "10px 16px",
            }}>
              <div style={{ fontSize: 10, color: "rgba(245,238,220,0.45)", letterSpacing: "0.08em" }}>NET PROFIT</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.gold }}>₹48,200</div>
              <div style={{ fontSize: 10, color: "#7ec47f" }}>↑ 14% vs last season</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Search & Filter ── */}
      <div style={{
        padding: "16px 20px 0",
        maxWidth: 900, margin: "0 auto",
        position: "sticky", top: 60, zIndex: 40, background: T.cream,
        paddingBottom: 12,
      }}>
        {/* Search */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          background: "#fff", borderRadius: 12, padding: "10px 14px",
          border: "1px solid #e0ddd4",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          marginBottom: 12,
        }}>
          <span style={{ fontSize: 15, color: T.textSoft }}>🔍</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search features..."
            style={{
              flex: 1, border: "none", outline: "none",
              fontSize: 14, color: T.textDark, background: "transparent",
              fontFamily: "'Outfit', sans-serif", fontWeight: 300,
            }}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{
              background: "none", border: "none", cursor: "pointer",
              color: T.textSoft, fontSize: 14,
            }}>✕</button>
          )}
        </div>

        {/* Category pills */}
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
          {categories.map(cat => {
            const col = categoryColors[cat] || { bg: T.mist, text: T.textMid };
            const active = filter === cat;
            return (
              <button key={cat} onClick={() => setFilter(cat)} style={{
                flexShrink: 0, padding: "5px 13px", borderRadius: 20,
                fontSize: 12, fontWeight: active ? 600 : 400, cursor: "pointer",
                fontFamily: "'Outfit', sans-serif",
                border: active ? "none" : "1px solid #e0ddd4",
                background: active ? T.moss : "#fff",
                color: active ? "#fff" : T.textSoft,
                transition: "all 0.18s",
                boxShadow: active ? `0 2px 8px ${T.moss}44` : "none",
              }}>{cat}</button>
            );
          })}
        </div>
      </div>

      {/* ── Cards Grid ── */}
      <main style={{ maxWidth: 900, margin: "0 auto", padding: "16px 20px 40px" }}>
        <div style={{
          fontSize: 12, color: T.textSoft, marginBottom: 12, fontWeight: 300,
        }}>{filtered.length} feature{filtered.length !== 1 ? "s" : ""} available</div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: T.textSoft }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🌿</div>
            <div style={{ fontFamily: "'Lora', serif", fontSize: 18, color: T.textMid }}>No features found</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>Try a different search or category</div>
          </div>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 14,
          }}>
            {filtered.map((f, i) => (
              <FeatureCard
                key={f.id}
                feature={f}
                onClick={setSelected}
                delay={i * 0.05}
              />
            ))}
          </div>
        )}
      </main>

      {/* ── Bottom Nav ── */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        background: "rgba(255,255,255,0.95)", backdropFilter: "blur(12px)",
        borderTop: "1px solid #e0ddd4",
        display: "flex", justifyContent: "space-around",
        padding: "10px 0 14px", zIndex: 45,
      }}>
        {[
          { icon: "🏠", label: "Home", active: true },
          { icon: "🌱", label: "My Crops" },
          { icon: "📊", label: "Reports" },
          { icon: "⚙️", label: "Settings" },
        ].map(n => (
          <button key={n.label} style={{
            background: "none", border: "none", cursor: "pointer",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
            fontFamily: "'Outfit', sans-serif",
          }}>
            <span style={{ fontSize: 20 }}>{n.icon}</span>
            <span style={{
              fontSize: 10, fontWeight: n.active ? 600 : 400,
              color: n.active ? T.moss : T.textSoft,
            }}>{n.label}</span>
            {n.active && <div style={{
              width: 4, height: 4, borderRadius: "50%", background: T.moss,
            }} />}
          </button>
        ))}
      </div>

      {/* ── Detail Panel ── */}
      {selected && (
        <DetailPanel feature={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

export default BhuPilot;